// Events: the sealed chain, filtered; and one event with its evidence (plan §7 evidence).
import { useEffect, useMemo, useState } from "react";
import { strokeToSvg, validateStroke, type Stroke } from "@vavr/core";
import { Badge, Banner, Button, Card, Select, Table, TextField } from "@vavr/ui";
import { api, blobUrl, dateTime, eventLabel, FLAG_LABEL, Link } from "../shared/index.ts";
import { useConsole, useGet, useAction, useBlob, download } from "./data.tsx";
import { LoadError, Loading, Toolbar, today, useNames } from "./common.tsx";
import { addDays, eventTime, zonedTime, type EventLike } from "./reports.ts";

type EventRow = EventLike & { event_hash: string; prev_hash: string; body_hash: string; anchor_id: string | null; group_id: string | null; device_sig: string; pre: EventLike["pre"] & Record<string, any> };

const TYPES = ["pickup", "dropoff", "transfer", "absence", "override", "correction", "note", "shift_start", "shift_end"];

export function flagBadges(flags: string[]) {
  return <span className="pmu-row pmu-c-flags">{flags.map((f) => <Badge key={f} tone={f === "restriction_hit" ? "restriction" : "warning"}>{FLAG_LABEL[f] ?? f}</Badge>)}</span>;
}

export function EventsList() {
  const { base, site } = useConsole();
  const { names, device, children } = useNames();
  const [f, setF] = useState(() => ({ from: addDays(today(site.tz), -6), to: today(site.tz), type: "all", child: "all" }));
  const path = useMemo(() => {
    const q = new URLSearchParams({ limit: "100" });
    if (f.from) q.set("since", String(zonedTime(f.from, site.tz)));
    if (f.to) q.set("until", String(zonedTime(addDays(f.to, 1), site.tz)));
    if (f.type !== "all") q.set("type", f.type);
    if (f.child !== "all") q.set("child", f.child);
    return `${base}/events?${q}`;
  }, [base, f, site.tz]);
  const first = useGet<{ events: EventRow[] }>(path);
  const [more, setMore] = useState<EventRow[]>([]);
  const [end, setEnd] = useState(false);
  const act = useAction();
  useEffect(() => { setMore([]); setEnd(false); }, [path]);
  const rows = [...(first.data?.events ?? []), ...more];
  const loadMore = () => act.run(async () => {
    const last = rows[rows.length - 1];
    if (!last) return;
    const r = await api<{ events: EventRow[] }>("GET", `${path}&before=${last.seq}`);
    setMore((m) => [...m, ...r.events]);
    if (r.events.length < 100) setEnd(true);
  });

  return (
    <div className="pmu-stack">
      <Toolbar>
        <TextField label="From" type="date" value={f.from} max={f.to} onChange={(e) => setF({ ...f, from: e.target.value })} />
        <TextField label="To" type="date" value={f.to} min={f.from} onChange={(e) => setF({ ...f, to: e.target.value })} />
        <Select label="Type" value={f.type} onValueChange={(v) => setF({ ...f, type: v ?? "all" })} options={[{ value: "all", label: "All types" }, ...TYPES.map((t) => ({ value: t, label: eventLabel(t) }))]} />
        <Select label="Child" value={f.child} onValueChange={(v) => setF({ ...f, child: v ?? "all" })} options={[{ value: "all", label: "All children" }, ...children.map((c: any) => ({ value: c.id, label: `${c.last}, ${c.first}` }))]} />
      </Toolbar>
      {first.error && <LoadError error={first.error} retry={first.reload} />}
      {first.loading ? <Loading /> : (
        <Table
          caption="Events, newest first" hideCaption rowKey={(e) => String(e.seq)} rows={rows} maxHeight="calc(100dvh - 240px)" empty="No events match."
          columns={[
            { key: "seq", header: "Record", width: "5rem", align: "end", render: (e) => <Link className="pmu-link" href={`/console/events/${e.id}`}>{e.seq}</Link> },
            { key: "t", header: "When", render: (e) => <span className="pmu-c-nowrap">{dateTime(eventTime(e), site.tz)}</span> },
            { key: "y", header: "What", rowHeader: true, render: (e) => eventLabel(e.type) },
            { key: "c", header: "Child", render: (e) => (e.child_id ? names.child(e.child_id) : "") },
            { key: "a", header: "Adult", render: (e) => (e.adult_id ? names.adult(e.adult_id) : e.pre.claimed_name ?? "") },
            { key: "s", header: "Staff", render: (e) => names.staff(e.staff_id) },
            { key: "d", header: "Device", render: (e) => device(e.device_id) },
            { key: "f", header: "Flags", render: (e) => flagBadges(e.server_flags) },
          ]}
        />
      )}
      {!first.loading && rows.length >= 100 && !end && <div><Button variant="secondary" loading={act.busy} onClick={loadMore}>Load older events</Button></div>}
    </div>
  );
}

export function EventDetail({ id }: { id: string }) {
  const { base, site, can } = useConsole();
  const { names, device } = useNames();
  const d = useGet<{ event: EventRow; anchor: { anchor_id: string; root_hash: string; seq: number } | null }>(`${base}/events/${id}`);
  const photo = useBlob(base, d.data?.event.pre.photo_sha256);
  const act = useAction();
  if (d.loading) return <Loading />;
  if (d.error || !d.data) return <LoadError error={d.error ?? new Error("Not found")} retry={d.reload} />;
  const { event: e, anchor } = d.data;
  const pre = e.pre;
  const off = e.clock_offset_ms;
  const offLabel = Math.abs(off) < 1000 ? "under a second" : `${Math.abs(Math.round(off / 1000))} s ${off > 0 ? "behind" : "ahead of"} the server`;

  return (
    <div className="pmu-stack">
      <p><Link className="pmu-link" href="/console/events">All events</Link></p>
      {e.server_flags.includes("restriction_hit") && <Banner tone="restriction" title="This handover involved a restricted person" />}
      <Card title={`${eventLabel(e.type)}${e.child_id ? `: ${names.child(e.child_id)}` : ""}`} description={`Record ${e.seq} in ${site.name}'s chain`}
        actions={<>
          {can("evidence.export") && <Button loading={act.busy} onClick={() => act.run(async () => {
            const pack = await api("GET", `${base}/events/${e.id}/evidence`);
            download(`vavr-evidence-${e.id}.json`, JSON.stringify(pack, null, 2), "application/json");
          }, { success: "Evidence file downloaded. The download is logged as a disclosure." })}>Download evidence</Button>}
          <Link className="pmu-button pmu-button--secondary" href="/verify">Verify a file</Link>
        </>}>
        {act.error && <Banner tone="danger" title={act.error} />}
        <div className="pmu-c-event">
          <dl className="pmu-kv">
            {e.child_id && <><dt>Child</dt><dd><Link className="pmu-link" href={`/console/children/${e.child_id}`}>{names.child(e.child_id)}</Link></dd></>}
            <dt>Adult</dt><dd>{e.adult_id ? names.adult(e.adult_id) : pre.claimed_name ? `${pre.claimed_name} (not on file)` : "None"}</dd>
            {pre.id_check && <><dt>ID checked</dt><dd>{String(pre.id_check.type).replace(/_/g, " ")}, ending {pre.id_check.last4}</dd></>}
            <dt>How they were identified</dt><dd>{AUTH[pre.auth_method ?? "none"] ?? pre.auth_method}</dd>
            <dt>Staff on duty</dt><dd>{names.staff(e.staff_id)}</dd>
            {pre.witness_staff_id && <><dt>Witness</dt><dd>{names.staff(pre.witness_staff_id)}</dd></>}
            <dt>Device</dt><dd>{device(e.device_id)}</dd>
            <dt>Device time</dt><dd>{fmtFull(e.device_ts, site.tz)}</dd>
            <dt>Server time</dt><dd>{fmtFull(e.server_ts, site.tz)}</dd>
            <dt>Clock offset</dt><dd>{offLabel} ({off} ms)</dd>
            <dt>Server checks</dt><dd>{e.server_flags.length ? flagBadges(e.server_flags) : "No flags"}</dd>
            {(pre.flags ?? []).length > 0 && <><dt>Recorded at the door</dt><dd>{(pre.flags ?? []).map((x: string) => x.replace(/_/g, " ")).join(", ")}</dd></>}
            {pre.reason && <><dt>Reason</dt><dd>{pre.reason}</dd></>}
            {pre.data && <><dt>Details</dt><dd className="pmu-mono pmu-small">{JSON.stringify(pre.data)}</dd></>}
            {pre.attestation && <><dt>Attestation shown</dt><dd>“{pre.attestation}”</dd></>}
            {pre.face && <><dt>Face detection</dt><dd>One face, confidence {(pre.face.conf / 10).toFixed(1)}% (presence only; no recognition)</dd></>}
          </dl>
          <div className="pmu-stack">
            {pre.photo_sha256 && (
              <figure className="pmu-c-photo">
                {photo ? <img src={photo} alt={`Photo taken at the door of ${e.adult_id ? names.adult(e.adult_id) : pre.claimed_name ?? "the adult"}`} /> : <Loading label="Loading the photo" />}
                <figcaption className="pmu-small pmu-muted">Photo taken at the door, for staff to compare by eye</figcaption>
              </figure>
            )}
            {pre.stroke_sha256 && <Signature base={base} sha={pre.stroke_sha256} />}
            {!pre.photo_sha256 && !pre.stroke_sha256 && <p className="pmu-muted">No photo or signature on this record.</p>}
          </div>
        </div>
      </Card>
      <Card title="Seal">
        <dl className="pmu-kv">
          <dt>Event hash</dt><dd className="pmu-mono pmu-small">{e.event_hash}</dd>
          <dt>Previous hash</dt><dd className="pmu-mono pmu-small">{e.prev_hash}</dd>
          <dt>Body hash</dt><dd className="pmu-mono pmu-small">{e.body_hash}</dd>
          <dt>Timestamp anchor</dt><dd>{anchor ? <><Badge tone="success" icon="check-circle">Anchored</Badge> <span className="pmu-small">in the hourly root through record {anchor.seq}</span><div className="pmu-mono pmu-small">{anchor.root_hash}</div></> : <Badge tone="info" icon="clock">Waiting for the hourly anchor</Badge>}</dd>
        </dl>
      </Card>
    </div>
  );
}

const AUTH: Record<string, string> = { tile: "Chose their photo", pin: "Their PIN", qr: "Guardian app QR pass", code: "One-time delegation code", id_check: "ID checked by staff", none: "Not identified" };
const fmtFull = (ts: number, tz: string) => new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "medium", timeZone: tz }).format(new Date(ts));

/** The signature drawn from the stroke JSON (the evidence), never from a stored image. */
function Signature({ base, sha }: { base: string; sha: string }) {
  const [svg, setSvg] = useState<string | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let live = true;
    (async () => {
      const url = await blobUrl(`${base}/blobs/${sha}`);
      try {
        const stroke = JSON.parse(await (await fetch(url)).text()) as Stroke;
        // Only numbers reach the SVG: the stroke is validated before it is rendered.
        if (validateStroke(stroke).length) throw new Error("bad stroke");
        if (live) setSvg(strokeToSvg(stroke, 4));
      } finally { URL.revokeObjectURL(url); }
    })().catch(() => live && setErr(true));
    return () => { live = false; };
  }, [base, sha]);
  if (err) return <Banner tone="warning" title="The signature could not be shown" />;
  if (!svg) return <Loading label="Loading the signature" />;
  return (
    <figure className="pmu-c-signature">
      <div role="img" aria-label="Signature drawn at the door" dangerouslySetInnerHTML={{ __html: svg }} />
      <figcaption className="pmu-small pmu-muted">Signature, drawn from the recorded strokes</figcaption>
    </figure>
  );
}

